<template>
  <div class="login-form max-w-md mx-auto bg-surface border border-line rounded-lg p-8">
    <h2 class="text-2xl font-bold text-accent-2 mb-6 text-center">
      Sign In to Popty
    </h2>

    <!-- WHERE THIS VOICE IS CAST (Tom, 2026-09-12). A booth-link-only artist
         with no Popty login used to land here blank. Now the page reads the
         booth it came from - the /r/:voiceId redirect or the tab's remembered
         booth - and says which course the casting names and which email to
         sign in with. Artist rights derive from the casting, not from the
         users page: services/voice-engine/casting-rights.cjs. -->
    <p v-if="boothCasting" class="booth-casting text-muted text-sm text-center mb-4">
      This booth is {{ boothCasting.displayName }}'s, cast on {{ boothCasting.courses.join(', ') || boothCasting.languageName }}.
      Sign in with the email the casting names<template v-if="boothCasting.emailHint">, {{ boothCasting.emailHint }},</template>
      to open the course pages and edit your lines.
    </p>

    <!-- Step 1: Email Entry -->
    <div v-if="step === 'email'" class="space-y-4">
      <div>
        <label class="block text-sm text-muted mb-2">Email Address</label>
        <input
          v-model="email"
          type="email"
          placeholder="you@example.com"
          class="w-full bg-canvas border border-line rounded-lg px-4 py-3 text-ink placeholder-faint focus:border-emerald-500 focus:outline-none"
          @keyup.enter="goToAuth"
        />
      </div>

      <button
        @click="goToAuth"
        :disabled="loading || !email"
        class="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-surface-2 disabled:text-faint text-white py-3 rounded-lg font-semibold transition-colors"
      >
        <span v-if="loading">Sending code...</span>
        <span v-else>Send Login Code</span>
      </button>

      <p v-if="error" class="text-danger text-sm text-center">
        {{ error }}
      </p>

      <div v-if="email" class="text-center">
        <button
          @click="step = 'password'; nextTick(() => passwordInput?.focus())"
          class="text-sm text-muted hover:text-accent-2 transition-colors"
        >
          Use password instead
        </button>
      </div>
    </div>

    <!-- Step 2: Password Entry -->
    <div v-else-if="step === 'password'" class="space-y-4">
      <p class="text-ink text-center mb-2">
        <strong class="text-accent-2">{{ email }}</strong>
      </p>

      <div>
        <label class="block text-sm text-muted mb-2">Password</label>
        <input
          ref="passwordInput"
          v-model="password"
          type="password"
          placeholder="Enter your password"
          class="w-full bg-canvas border border-line rounded-lg px-4 py-3 text-ink placeholder-faint focus:border-emerald-500 focus:outline-none"
          @keyup.enter="handlePasswordLogin"
        />
      </div>

      <button
        @click="handlePasswordLogin"
        :disabled="loading || !password"
        class="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-surface-2 disabled:text-faint text-white py-3 rounded-lg font-semibold transition-colors"
      >
        <span v-if="loading">Signing in...</span>
        <span v-else>Sign In</span>
      </button>

      <p v-if="error" class="text-danger text-sm text-center">
        {{ error }}
      </p>

      <div class="flex justify-between text-sm">
        <button
          @click="switchToOTP"
          :disabled="loading"
          class="text-muted hover:text-accent-2 transition-colors"
        >
          Use login code instead
        </button>
        <button
          @click="reset"
          class="text-muted hover:text-ink transition-colors"
        >
          Different email
        </button>
      </div>
    </div>

    <!-- Step 3: OTP Code Entry -->
    <div v-else-if="step === 'code'" class="space-y-4">
      <p class="text-ink text-center mb-4">
        We sent a 6-digit code to<br/>
        <strong class="text-accent-2">{{ email }}</strong>
      </p>

      <div>
        <label class="block text-sm text-muted mb-2">Enter Code</label>
        <input
          ref="codeInput"
          v-model="code"
          type="text"
          maxlength="6"
          placeholder="000000"
          class="w-full bg-canvas border border-line rounded-lg px-4 py-3 text-ink placeholder-faint focus:border-emerald-500 focus:outline-none text-center text-2xl tracking-widest font-mono"
          @keyup.enter="handleVerifyOTP"
        />
      </div>

      <button
        @click="handleVerifyOTP"
        :disabled="loading || code.length < 6"
        class="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-surface-2 disabled:text-faint text-white py-3 rounded-lg font-semibold transition-colors"
      >
        <span v-if="loading">Verifying...</span>
        <span v-else>Sign In</span>
      </button>

      <p v-if="error" class="text-danger text-sm text-center">
        {{ error }}
      </p>

      <div class="flex justify-between text-sm">
        <button
          @click="handleSendOTP"
          :disabled="loading"
          class="text-muted hover:text-accent-2 transition-colors"
        >
          Resend code
        </button>
        <button
          @click="switchToPassword"
          class="text-muted hover:text-ink transition-colors"
        >
          Use password
        </button>
        <button
          @click="reset"
          class="text-muted hover:text-ink transition-colors"
        >
          Different email
        </button>
      </div>
    </div>

    <!-- Step 4: Invite Code (shown when authenticated but no dashboard access) -->
    <div v-else-if="step === 'invite-code'" class="space-y-4">
      <p class="text-ink text-center mb-2">
        Signed in as <strong class="text-accent-2">{{ email }}</strong>
      </p>
      <p class="text-muted text-sm text-center mb-4">
        Enter your invite code to get access.
      </p>

      <div>
        <input
          ref="inviteInput"
          v-model="inviteCode"
          type="text"
          maxlength="8"
          placeholder="XXXXXXXX"
          class="w-full bg-canvas border border-line rounded-lg px-4 py-3 text-ink placeholder-faint focus:border-emerald-500 focus:outline-none text-center text-2xl tracking-widest font-mono uppercase"
          @keyup.enter="handleRedeemCode"
        />
      </div>

      <button
        @click="handleRedeemCode"
        :disabled="redeemLoading || inviteCode.length < 4"
        class="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-surface-2 disabled:text-faint text-white py-3 rounded-lg font-semibold transition-colors"
      >
        <span v-if="redeemLoading">Redeeming...</span>
        <span v-else>Redeem Code</span>
      </button>

      <p v-if="redeemError" class="text-danger text-sm text-center">
        {{ redeemError }}
      </p>

      <div class="text-center text-sm">
        <button
          @click="reset"
          class="text-muted hover:text-ink transition-colors"
        >
          Different email
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '../composables/useAuth'
import { getApiUrl } from '../services/api'
import { rememberedBooth } from '../views/recordist/booth-pointer.js'
import { withBoothKey, rememberBoothKey } from '../services/recordingApi'

const router = useRouter()

const boothCasting = ref(null)
onMounted(async () => {
  const redirect = String(router.currentRoute.value?.query?.redirect || '')
  const fromLink = redirect.match(/^\/r\/([^/?#]+)/)
  const voiceId = (fromLink && decodeURIComponent(fromLink[1])) || rememberedBooth()
  if (!voiceId) return
  // A private booth link (job #380) carries its key: keep it, and present it.
  const linkKey = fromLink ? new URLSearchParams(redirect.split('?')[1] || '').get('k') : null
  if (linkKey) rememberBoothKey(voiceId, linkKey)
  try {
    const resp = await fetch(withBoothKey(`${getApiUrl()}/api/recording/voice/${encodeURIComponent(voiceId)}/casting`, voiceId))
    if (resp.ok) boothCasting.value = await resp.json()
  } catch { /* no line rather than a broken page */ }
})
const { sendOTP, verifyOTP, signInWithPassword, loading, error, hasDashboardAccess, refreshAccess, accessLookupWasAuthoritative } = useAuth()

const step = ref('email')
const email = ref('')
const password = ref('')
const code = ref('')
const inviteCode = ref('')
const redeemLoading = ref(false)
const redeemError = ref(null)
const codeInput = ref(null)
const passwordInput = ref(null)
const inviteInput = ref(null)

async function goToAuth() {
  if (!email.value) return
  error.value = null
  // OTP is the default — send code immediately
  try {
    await sendOTP(email.value)
    step.value = 'code'
    code.value = ''
    await nextTick()
    codeInput.value?.focus()
  } catch (err) {
    // Error handled in composable
  }
}

async function handlePasswordLogin() {
  if (!password.value) return

  try {
    await signInWithPassword(email.value, password.value)
    if (!hasDashboardAccess.value) {
      // Only invite-wall on a DEFINITE "no row". A failed lookup (machine
      // unreachable) keeps the useAuth error visible on this step so the
      // user can retry — an admin must never be asked for an invite code
      // because a tunnel was down.
      if (!accessLookupWasAuthoritative()) return
      error.value = null
      step.value = 'invite-code'
      inviteCode.value = ''
      await nextTick()
      inviteInput.value?.focus()
      return
    }
    const redirect = router.currentRoute.value.query.redirect || '/'
    window.location.href = redirect
  } catch (err) {
    // If "Invalid login credentials", stay on password step — user can retry or switch to OTP
  }
}

function switchToPassword() {
  error.value = null
  step.value = 'password'
  password.value = ''
  nextTick(() => passwordInput.value?.focus())
}

async function switchToOTP() {
  error.value = null
  try {
    await sendOTP(email.value)
    step.value = 'code'
    code.value = ''
    await nextTick()
    codeInput.value?.focus()
  } catch (err) {
    // Error handled in composable
  }
}

async function handleSendOTP() {
  try {
    await sendOTP(email.value)
    code.value = ''
  } catch (err) {
    // Error handled in composable
  }
}

async function handleVerifyOTP() {
  if (code.value.length < 6) return

  try {
    await verifyOTP(email.value, code.value)
    if (!hasDashboardAccess.value) {
      // Same guard as the password path: network miss ≠ no access.
      if (!accessLookupWasAuthoritative()) return
      error.value = null
      step.value = 'invite-code'
      inviteCode.value = ''
      await nextTick()
      inviteInput.value?.focus()
      return
    }
    const redirect = router.currentRoute.value.query.redirect || '/'
    window.location.href = redirect
  } catch (err) {
    // Error handled in composable
  }
}

async function handleRedeemCode() {
  if (!inviteCode.value || inviteCode.value.length < 4) return
  redeemLoading.value = true
  redeemError.value = null

  try {
    const resp = await fetch(`${getApiUrl()}/api/auth/invite-codes/redeem`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: inviteCode.value, email: email.value })
    })
    const data = await resp.json()

    if (!resp.ok) {
      redeemError.value = data.error || 'Failed to redeem code'
      return
    }

    // Refresh auth state in memory — now they have a dashboard_users row
    await refreshAccess(email.value)
    router.push(router.currentRoute.value.query.redirect || '/')
  } catch (err) {
    redeemError.value = 'Connection error. Please try again.'
  } finally {
    redeemLoading.value = false
  }
}

function reset() {
  step.value = 'email'
  password.value = ''
  code.value = ''
  error.value = null
}
</script>
